// Shared UI primitives - all in one file for simplicity
import React, { ReactNode, CSSProperties } from 'react';

export const T = {
  bg: '#080706', surface: '#0f0e0c', panel: '#141310', card: '#1a1815', lift: '#201e1a',
  border: '#2a2620', borderHi: '#3d3830',
  copper: '#e8935a', copperLt: '#f5b07a', copperDim: '#7a4c2e',
  sage: '#7eb89a', sapphire: '#5b9bd5', teal: '#4db8b0', amber: '#d4a847',
  crimson: '#c0454a', lavender: '#9b7fd4',
  text: '#f0ebe4', sub: '#8a8178', dim: '#4a4540',
};

export const SEV_COLOR: Record<string, string> = {
  critical: T.crimson, high: '#e87a3a', medium: T.amber,
  low: T.sapphire, info: T.sub,
};

export const RISK_COLOR: Record<string, string> = {
  LOW: T.sage, MEDIUM: T.amber, HIGH: '#e87a3a', CRITICAL: T.crimson,
};

export const AGENT_COLOR: Record<string, string> = {
  provenance: T.copper, supply: T.sage, legal: T.lavender,
  finance: T.amber, procurement: T.amber, audit: T.sub,
};

export const STATUS_COLOR: Record<string, string> = {
  active: T.sage, idle: T.sub, negotiating: T.amber,
  alert: T.crimson, error: T.crimson, paused: T.dim,
};

// ── Chip ──────────────────────────────────────────────
export function Chip({ label, color, dot, small }: { key?: string | number; label: string; color: string; dot?: boolean; small?: boolean }) {
  return (
    <span style={{
      fontSize: small ? 8 : 9, fontWeight: 700, letterSpacing: .7, textTransform: 'uppercase',
      color, background: `${color}18`, border: `1px solid ${color}35`,
      borderRadius: 5, padding: small ? '1px 6px' : '2px 9px',
      display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
    }}>
      {dot && <span style={{ width: 5, height: 5, borderRadius: '50%', background: color, animation: 'pulse 2s infinite' }} />}
      {label}
    </span>
  );
}

// ── Section header ────────────────────────────────────
export function SectionHeader({
  title, sub, right,
}: { title: string; sub?: string; right?: ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
      <div>
        <h2 style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: 22, color: T.text, letterSpacing: -.3 }}>
          {title}
        </h2>
        {sub && <p style={{ color: T.sub, fontSize: 12, marginTop: 4 }}>{sub}</p>}
      </div>
      {right && <div style={{ flexShrink: 0 }}>{right}</div>}
    </div>
  );
}

// ── Card ──────────────────────────────────────────────
export function Card({ children, style }: { key?: string | number; children: ReactNode; style?: CSSProperties }) {
  return (
    <div style={{
      background: T.panel, border: `1px solid ${T.border}`,
      borderRadius: 12, padding: 20,
      ...style,
    }}>
      {children}
    </div>
  );
}

// ── Score ring ────────────────────────────────────────
export function ScoreRing({ score, size = 56 }: { score: number; size?: number }) {
  const r = (size / 2) - 5;
  const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  const color = score >= 85 ? T.sage : score >= 70 ? T.amber : score >= 50 ? '#e87a3a' : T.crimson;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={T.border} strokeWidth={4} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={4}
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round" style={{ transition: 'stroke-dashoffset .5s ease' }}
        />
      </svg>
      <div style={{
        position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: size * 0.22, color,
      }}>
        {score}
      </div>
    </div>
  );
}

// ── Skeleton loader ───────────────────────────────────
export function Skeleton({ w = '100%', h = 16, r = 6, style: extraStyle }: { key?: string | number; w?: string | number; h?: number; r?: number; style?: React.CSSProperties }) {
  return (
    <div style={{
      width: w, height: h, borderRadius: r,
      background: `linear-gradient(90deg, ${T.card} 25%, ${T.lift} 50%, ${T.card} 75%)`,
      backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite',
    }} />
  );
}

// ── Error box ─────────────────────────────────────────
export function ErrorBox({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div style={{
      background: `${T.crimson}10`, border: `1px solid ${T.crimson}30`,
      borderRadius: 10, padding: '16px 20px', color: T.crimson, fontSize: 13,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    }}>
      <span>⚠ {message}</span>
      {onRetry && (
        <button onClick={onRetry} style={{
          background: `${T.crimson}20`, border: `1px solid ${T.crimson}40`,
          color: T.crimson, borderRadius: 7, padding: '5px 12px',
          cursor: 'pointer', fontSize: 11, fontWeight: 600,
        }}>
          Retry
        </button>
      )}
    </div>
  );
}

// ── Shared formatting utilities ───────────────────────
/** Short date: "Jan 5, 2025" */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Short date without year: "Jan 5" */
export function fmtDateShort(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Date + time: "Jan 5, 2025, 3:42 PM" */
export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** Relative time: "2 hours ago", "just now", etc. */
export function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000)  return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  if (diff < 604_800_000) return `${Math.floor(diff / 86_400_000)}d ago`;
  return fmtDate(iso);
}

/** Locale number with commas: "1,234,567" */
export function fmtNum(n: number | null | undefined): string {
  if (n == null) return '—';
  return n.toLocaleString();
}

// ── Global CSS (inject once) ──────────────────────────
export const GLOBAL_CSS = `
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.3} }
  @keyframes fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
  @keyframes spin { to{transform:rotate(360deg)} }
  .fade-up { animation: fadeUp .25s ease; }
  input, button, textarea { font-family: 'DM Sans', sans-serif; }
  a { color: inherit; text-decoration: none; }
`;
